import React, { useEffect, useState } from 'react';
import { quickdraw } from '../../../../../meta/TryhardProductInfo';
import { motion, MotionValue, useMotionValue, useTransform } from 'motion/react';
import { chevrons, SVGData, upChevronPath } from '../../../../../../svg/SVGData';
import { animate } from 'motion';

interface ArrowSVGProps {
    scale: MotionValue,
    x: MotionValue,
    y: MotionValue,
    rotation: MotionValue
}

const ArrowSVG: React.FC<ArrowSVGProps> = ({ scale, x, y, rotation }) => {
    return (
        <motion.svg
            layout
            viewBox={"0 0 512 512"}
            fill={'white'}
            style={{ scale, rotateZ: rotation, x, y }}
        >
            <path d={upChevronPath} />
        </motion.svg>
    )
}

const orientationStyles = {
    'up': 'px-2 py-1 -translate-x-1/2',
    'down': 'px-2 py-1 -translate-x-1/2',
    'right': 'px-2 py-2 -translate-y-1/2',
    'left': 'px-2 py-2',
}

interface ArrowButtonProps {
    cb: Function,
    data: SVGData,
    orientation: 'up' | 'down' | 'left' | 'right'
}

const ArrowButton: React.FC<ArrowButtonProps> = ({ cb, data, orientation }) => {
    const scale = useMotionValue(1);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotation = useMotionValue('0deg');

    if (orientation === 'down') {
        rotation.set('180deg');
    } else if (orientation === 'left') {
        rotation.set('270deg');
    } else if (orientation === 'right') {
        rotation.set('90deg');
    }

    let jumpMagnitude = -2;
    let selectMagnitude = -2;
    let animateDuration = .08;

    if (orientation === 'down' || orientation === 'right') {
        jumpMagnitude = -jumpMagnitude;
        selectMagnitude = -selectMagnitude;
    }

    let translation: MotionValue | null = null;

    if (orientation === 'up' || orientation === 'down') {
        translation = y;
    } else {
        translation = x;
    }

    const handleOnClick = () => {
        cb();
        animate(translation, [jumpMagnitude, jumpMagnitude + selectMagnitude, jumpMagnitude], { duration: animateDuration })
    }

    const onPointerEnter = () => {
        animate(scale, 1.1, { duration: animateDuration });
        animate(translation, [jumpMagnitude], { duration: animateDuration });
    }

    const onPointerLeave = () => {
        animate(scale, 1, { duration: animateDuration });
        animate(translation, 0, { duration: animateDuration });
    }

    return (
        <div
            onClick={handleOnClick}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            className={'bg-opacity-80 bg-red-600 hover:cursor-pointer flex justify-center items-center size-12 ' + (orientationStyles[orientation])}
        >
            <ArrowSVG rotation={rotation} scale={scale} x={x} y={y} />
        </div>
    )
}

const Arrows: React.FC<any> = ({ upCb, downCb, rightCb, leftCb }) => {
    return (
        <>
            <div className='absolute top-0 inset-x-1/2'>
                <ArrowButton cb={upCb} data={chevrons.up} orientation='up' />
            </div>
            <div className='absolute bottom-0 inset-x-1/2'>
                <ArrowButton cb={downCb} data={chevrons.up} orientation='down' />
            </div>
            <div className='absolute right-0 inset-y-1/2'>
                <ArrowButton cb={rightCb} data={chevrons.up} orientation='right' />
            </div>
            <div className='absolute left-0 inset-y-1/'>
                <ArrowButton cb={leftCb} data={chevrons.up} orientation='left' />
            </div>
        </>
    )
}

const CursorCircle: React.FC<{ index: number, cursor: number }> = ({ index, cursor }) => {
    const fillColor = useMotionValue('#94a3b8');
    const opacity = useMotionValue(1);

    if (index === cursor) {
        animate(fillColor, '#374151');
        animate(opacity, 1);
    } else {
        animate(fillColor, '#cbd5e1');
        animate(opacity, .75);
    }

    return (
        <motion.svg
            style={{ opacity }}
            viewBox='0 0 64 64'
            fill='none'
        >
            <motion.circle
                cx='50%' cy='50%' r='40%'
                style={{ fill: fillColor }}
            />
            <motion.circle
                cx='50%' cy='50%' r='40%'
                stroke='#e2e8f0'
                strokeWidth='5%'
            />

        </motion.svg>
    )
}

interface ColorLabalProps {
    color: string,
    index: number,
    cursor: number
}

const ColorLabel: React.FC<ColorLabalProps> = ({color, cursor, index }) => {
    const scaleSlider = useMotionValue(0);
    const height = useTransform(() => `${scaleSlider.get() * 2.5}rem`);
    const width = useTransform(() => `${scaleSlider.get() * 100}%`)
    const fontSize = useTransform(() => `${scaleSlider.get() * 1.875}rem`)

    const scaleDiff = Math.abs(index - cursor) * .3;
    animate(scaleSlider, (1 - scaleDiff))

    return (
        <motion.div
            className={'flex items-center w-full pt-1 gap-x 1 bg-gray-100 bg-opacity-50'}
            style={{
                width, height
            }}
        >
            <motion.div style={{ backgroundColor: color }} className='h-2/3 rounded-sm aspect-square' />
            <motion.span
                style={{
                    filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, .4))',
                    fontSize: fontSize
                }}
                className='italic text-teko text-gray-800 font-semibold capitalize drop-shadow-sm mt-1 px-2'
            >
                {color}
            </motion.span>
        </motion.div>
    )
}

const LabelWindow: React.FC<{ colors: string[], cursor: number }> = ({ colors, cursor }) => {
    const d = `${-(cursor * (3 * .225))}rem`
    const translateY = useMotionValue(d);
    animate(translateY, d)

    return (
        <div
            className='absolute top-0 right-0 w-1/3 h-[6rem] overflow-clip'
        >
            <motion.div layout className='flex flex-col items-end w-full' style={{ translateY }} >
                {colors.map((c, i) => <ColorLabel key={i} index={i} color={c} cursor={cursor} />)}
            </motion.div>
        </div>
    )
}

const TryhardBoxesCard: React.FC = () => {
    // Products
    const [cursorX, setColorCursorX] = useState(0);
    const [cursorY, setCursorY] = useState(0);

    const handleOnClickUp = () => setCursorY(cursorY + 1)
    const handleOnClickDown = () => setCursorY(cursorY - 1)
    const handleOnClickRight = () => setColorCursorX(cursorX + 1)
    const handleOnClickLeft = () => setColorCursorX(cursorX - 1)

    const { colors, photos } = quickdraw;
    const { material } = photos;

    let src = '';

    const root = '/thg/quickdraw'
    const realY = Math.abs(cursorY % colors.length);
    const realX = cursorX % material.length
    const color = colors.at(realY) || '';
    const photo = material.at(realX);
    src = `${root}/${color}/${photo}`;

    const colorLabelFill = useMotionValue('black');

    useEffect(() => {
        animate(colorLabelFill, color, { duration: 5 })
    }, [cursorY])

    return (
        <div
            className='flex justify-center items-center h-96 w-72 sm:w-96 bg-white shadow-2xl bg-opacity-40 relative'
        >
            <div className='w-3/5 h-3/5 flex justify-center'>
                <img className='object-contain' src={src} />
            </div>

            <Arrows
                upCb={handleOnClickUp} downCb={handleOnClickDown}
                leftCb={handleOnClickLeft} rightCb={handleOnClickRight} />

            <LabelWindow cursor={realY} colors={colors} />

            {/* Nav clues */}
            <div className='flex gap-x-2 sm:gap-x-3 p-2 h-9 sm:px-3 sm:py-3 sm:h-12 absolute bottom-0 right-0 z-50'>
                {[...Array(material.length).keys()].map((v) => <CursorCircle key={v} index={v} cursor={realX} />)}
            </div>
        </div>
    )
}

export default TryhardBoxesCard;